import { useState, useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  MdCalendarToday, 
  MdPayment, 
  MdWork, 
  MdPerson, 
  MdNotifications,
  MdLogout,
  MdPeople,
  MdSettings,
  MdMessage,
  MdCheckCircle,
  MdAttachMoney,
  MdAssignment,
  MdDone
} from 'react-icons/md';
import { getUser, logout } from '../services/auth';
import { t } from '../utils/i18n';
import logoImage from '../assets/babysit_logo.png';
import backSideFlowers from '../assets/back_side_flowers.png';
import api from '../services/api';
import AnimatedBackground from './AnimatedBackground';
import SubtleGridBackground from './SubtleGridBackground';
import ConfirmDialog from './ConfirmDialog';
import { useIsMobile } from '../hooks/useIsMobile';

export default function DashboardLayout() {
  const isMobile = useIsMobile();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const notificationsButtonRef = useRef(null);
  const notificationsPanelRef = useRef(null);
  const messagesButtonRef = useRef(null);
  const messagesPanelRef = useRef(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoadingNotifications(true);
      try {
        const response = await api.get('/notifications');
        // Backend returns { notifications: [...], pagination: {...}, unreadCount: ... }
        const notificationsData = response.data?.notifications || (Array.isArray(response.data) ? response.data : []);
        // Normalize notification format: backend uses isRead, frontend expects read
        const normalizedNotifications = notificationsData.map(notif => ({
          ...notif,
          read: notif.isRead !== undefined ? notif.isRead : notif.read,
          type: notif.type || 'default',
          message: notif.message || notif.title || 'Notification',
          createdAt: notif.createdAt || notif.created_at || new Date().toISOString()
        }));
        setNotifications(normalizedNotifications);
      } catch (error) {
        console.error('Error loading notifications:', error);
        // Fallback to empty array instead of mock data
        setNotifications([]);
      } finally {
        setIsLoadingNotifications(false);
      }
    };

    fetchNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch conversations for unread messages count
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await api.get('/conversations').catch(() => ({ data: [] }));
        setConversations(response.data || []);
      } catch (error) {
        console.error('Error loading conversations:', error);
        setConversations([]);
      }
    };

    fetchConversations();
    // Refresh conversations every 10 seconds for real-time updates
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  // Desktop only: close popups on outside click
  useEffect(() => {
    if (isMobile || (!notificationsOpen && !messagesOpen)) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      const inMessages = messagesButtonRef.current?.contains(target) || messagesPanelRef.current?.contains(target);
      const inNotifications = notificationsButtonRef.current?.contains(target) || notificationsPanelRef.current?.contains(target);
      if (!inMessages && !inNotifications) {
        setNotificationsOpen(false);
        setMessagesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationsOpen, messagesOpen, isMobile]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
        setMessagesOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    setNotificationsOpen(false);
    setMessagesOpen(false);
  }, [location.pathname]);

  // Full sidebar items for desktop (all 7 items)
  const desktopMenuItems = [
    { path: '/dashboard/calendar', icon: MdCalendarToday, label: t('nav.calendar') || 'Calendar' },
    { path: '/dashboard/payments', icon: MdPayment, label: t('nav.payments') || 'Payments' },
    { path: '/dashboard/friends', icon: MdPeople, label: t('nav.friends') || 'Friends' },
    { path: '/dashboard/conversations', icon: MdMessage, label: t('nav.conversations') || 'Messages' },
    { path: '/dashboard/jobs', icon: MdWork, label: t('nav.jobs') || 'Oglasi' },
    { path: '/dashboard/profile', icon: MdPerson, label: t('nav.profile') || 'Profile' },
    { path: '/dashboard/settings', icon: MdSettings, label: t('nav.settings') || 'Settings' },
  ];
  // Mobile bottom tab: 5 items, Settings merged
  const mobileMenuItems = [
    { path: '/dashboard/calendar', icon: MdCalendarToday, label: t('nav.calendar') || 'Calendar' },
    { path: '/dashboard/payments', icon: MdPayment, label: t('nav.payments') || 'Payments' },
    { path: '/dashboard/friends', icon: MdPeople, label: t('nav.friends') || 'Friends' },
    { path: '/dashboard/jobs', icon: MdWork, label: t('nav.jobs') || 'Oglasi' },
    { path: '/dashboard/settings', icon: MdSettings, label: t('nav.settings') || 'Podešavanja', isSettings: true },
  ];

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard/settings') {
      return location.pathname === path || location.pathname.startsWith(path + '/') || location.pathname.includes('/profile');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  // For desktop: simple match (profile and settings are separate)
  const isActiveDesktop = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );
  const previewNotifications = useMemo(
    () => notifications.slice(0, 4),
    [notifications]
  );
  const unreadMessagesCount = useMemo(() => {
    // Count unread conversations using backend unreadCount
    // Only count conversations that actually have unreadCount > 0
    const total = conversations.reduce((sum, conv) => {
      // Prefer backend unreadCount when available (including 0)
      if (conv.unreadCount !== undefined && conv.unreadCount !== null) {
        const n = Number(conv.unreadCount) || 0;
        return sum + (n > 0 ? n : 0);
      }
      // Fallback: if last message is from other user and not marked as read, count as 1
      const lastMessage = conv.lastMessage;
      if (lastMessage && lastMessage.senderId !== user?.id && lastMessage.read !== true) {
        return sum + 1;
      }
      return sum;
    }, 0);
    
    // Double-check: if total is 0, make sure we return 0 (not undefined or null)
    return total > 0 ? total : 0;
  }, [conversations, user]);
  const previewConversations = useMemo(() => {
    // Show unread conversations
    return conversations
      .filter((conv) => {
        // Prefer backend unreadCount when available (including 0)
        if (conv.unreadCount !== undefined && conv.unreadCount !== null) {
          return Number(conv.unreadCount) > 0;
        }
        // Otherwise check last message - if last message is from other user, consider it unread
        const lastMessage = conv.lastMessage;
        if (!lastMessage) {
          // No messages in conversation, not unread
          return false;
        }
        // If last message is from current user, conversation is read
        if (lastMessage.senderId === user?.id) {
          return false;
        }
        // If last message is from other user and doesn't have read property set to true, consider it unread
        return lastMessage.read !== true;
      })
      .sort((a, b) => {
        const aTime = a.lastMessage?.createdAt || a.createdAt || 0;
        const bTime = b.lastMessage?.createdAt || b.createdAt || 0;
        return new Date(bTime) - new Date(aTime);
      })
      .slice(0, 5);
  }, [conversations, user]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'job_accepted':
        return <MdCheckCircle className="text-green-600 dark:text-green-400 text-xl" />;
      case 'payment_received':
        return <MdAttachMoney className="text-green-600 dark:text-green-400 text-xl" />;
      case 'job_posted':
        return <MdAssignment className="text-blue-600 dark:text-blue-400 text-xl" />;
      case 'job_completed':
        return <MdDone className="text-primary-600 dark:text-primary-400 text-xl" />;
      default:
        return <MdNotifications className="text-gray-600 dark:text-gray-400 text-xl" />;
    }
  };

  // On mobile, conversation thread is full screen (no top bar, no bottom tab)
  const isConversationThreadMobile =
    isMobile && /^\/dashboard\/conversations\/[^/]+$/.test(location.pathname);

  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/calendar')) return t('nav.calendar') || 'Calendar';
    if (path.includes('/payments')) return t('nav.payments') || 'Payments';
    if (path.includes('/jobs')) return t('nav.jobs') || 'Jobs';
    if (path.includes('/friends')) return t('nav.friends') || 'Friends';
    if (path.includes('/conversations')) return t('nav.conversations') || 'Messages';
    if (path.includes('/profile')) return t('nav.profile') || 'Profile';
    if (path.includes('/settings')) return t('nav.settings') || 'Settings';
    if (path.includes('/notifications')) return t('notifications.title') || 'Notifications';
    return '';
  };

  // Full-screen conversation thread on mobile: only outlet, no header/sidebar/bottom bar
  if (isConversationThreadMobile) {
    return (
      <div className="h-screen w-full bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden relative">
        <AnimatedBackground />
        <SubtleGridBackground opacityClass="opacity-[0.02] dark:opacity-[0.03]" />
        <main className="flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden relative">
      {/* Animated Background - Full Width */}
      <AnimatedBackground />
      {/* Subtle Grid (same style as Landing) */}
      <SubtleGridBackground opacityClass="opacity-[0.02] dark:opacity-[0.03]" />
      
      {/* Top Bar - Full Width */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 flex-shrink-0 h-16 w-full z-30 relative">
        <div className="px-4 h-full flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Logo and App Name - Always visible */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <img src={logoImage} alt="Logo" className="h-8 w-auto flex-shrink-0" />
              <span className="hidden lg:block text-lg font-semibold text-primary-600 dark:text-primary-300 leading-none">
                Nannity
              </span>
              {/* Mobile: Show page title */}
              <h1 className="lg:hidden text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {getPageTitle()}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* User Info */}
            <div className="hidden lg:flex items-center gap-3 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              onClick={() => navigate('/dashboard/profile')}
            >
              <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 overflow-hidden">
                {user?.photoUrl || user?.profileImage ? (
                  <img src={user.photoUrl || user.profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  user?.firstName?.[0]?.toUpperCase() || 'U'
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate leading-tight">
                  {user?.email}
                </p>
              </div>
            </div>
            {/* Messages Icon */}
            <div className="relative">
              <button
                ref={messagesButtonRef}
                onClick={() => {
                  if (isMobile) {
                    navigate('/dashboard/conversations');
                    return;
                  }
                  setMessagesOpen((prev) => !prev);
                  setNotificationsOpen(false);
                }}
                className="relative p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={t('nav.conversations') || 'Messages'}
              >
                <MdMessage className="text-xl text-gray-700 dark:text-gray-200" />
                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-blue-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                    {unreadMessagesCount}
                  </span>
                )}
              </button>

              {!isMobile && messagesOpen && (
                <div
                  ref={messagesPanelRef}
                  className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl z-50"
                >
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {t('conversations.title') || 'Poruke'}
                    </p>
                    {unreadMessagesCount > 0 && (
                      <span className="text-xs font-semibold text-blue-600 dark:text-blue-300">
                        {unreadMessagesCount} {t('conversations.unread') || 'nepročitanih'}
                      </span>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                    {previewConversations.length === 0 ? (
                      <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('conversations.noUnreadMessages') || 'Nema nepročitanih poruka'}
                      </div>
                    ) : (
                      previewConversations.map((conv) => {
                        const otherUser = conv.otherUser || conv.participants?.find(p => p.id !== user?.id);
                        const otherUserName = otherUser?.name || 
                          (otherUser?.firstName && otherUser?.lastName ? `${otherUser.firstName} ${otherUser.lastName}`.trim() : null) ||
                          otherUser?.email || 
                          'Unknown';
                        const lastMessage = conv.lastMessage;
                        return (
                          <div
                            key={conv.id}
                            className="px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            onClick={() => {
                              setMessagesOpen(false);
                              navigate(`/dashboard/conversations/${conv.id}`);
                            }}
                          >
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 overflow-hidden">
                              {otherUser?.photoUrl || otherUser?.photo_url || otherUser?.profileImage ? (
                                <img 
                                  src={otherUser.photoUrl || otherUser.photo_url || otherUser.profileImage} 
                                  alt={otherUserName}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full flex items-center justify-center ${otherUser?.photoUrl || otherUser?.photo_url || otherUser?.profileImage ? 'hidden' : ''}`}>
                                {otherUserName[0]?.toUpperCase() || 'U'}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {otherUserName}
                              </p>
                              {lastMessage && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-1">
                                  {lastMessage.content}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setMessagesOpen(false);
                        navigate('/dashboard/conversations');
                      }}
                      className="w-full text-sm text-center text-primary-600 dark:text-primary-300 hover:text-primary-700 dark:hover:text-primary-200 font-semibold"
                    >
                      {t('conversations.viewAll') || 'Vidi sve poruke'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications Icon */}
            <div className="relative">
              <button
                ref={notificationsButtonRef}
                onClick={async () => {
                  if (isMobile) {
                    navigate('/dashboard/notifications');
                    return;
                  }
                  setNotificationsOpen((prev) => !prev);
                  setMessagesOpen(false);
                  // Mark notifications as read when opening notifications panel
                  if (!notificationsOpen) {
                    try {
                      // Mark all notifications as read
                      await api.put('/notifications/read-all');
                      // Refresh notifications
                      const response = await api.get('/notifications');
                      const notificationsData = response.data?.notifications || (Array.isArray(response.data) ? response.data : []);
                      const normalizedNotifications = notificationsData.map(notif => ({
                        ...notif,
                        read: notif.isRead !== undefined ? notif.isRead : notif.read,
                        type: notif.type || 'default',
                        message: notif.message || notif.title || 'Notification',
                        createdAt: notif.createdAt || notif.created_at || new Date().toISOString()
                      }));
                      setNotifications(normalizedNotifications);
                    } catch (error) {
                      console.error('Error marking notifications as read:', error);
                    }
                  }
                }}
                className="relative p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={t('nav.notifications') || 'Notifications'}
              >
                <MdNotifications className="text-xl text-gray-700 dark:text-gray-200" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                    {unreadNotifications}
                  </span>
                )}
              </button>

              {!isMobile && notificationsOpen && (
                <div
                  ref={notificationsPanelRef}
                  className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl z-50"
                >
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {t('notifications.title') || 'Notifications'}
                    </p>
                    {unreadNotifications > 0 && (
                      <span className="text-xs font-semibold text-primary-600 dark:text-primary-300">
                        {unreadNotifications} {t('notifications.unread') || 'unread'}
                      </span>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                    {isLoadingNotifications ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      </div>
                    ) : previewNotifications.length === 0 ? (
                      <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('notifications.noNotifications') || 'No notifications'}
                      </div>
                    ) : (
                      previewNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            !notification.read ? 'bg-primary-50/60 dark:bg-primary-900/10' : ''
                          }`}
                          onClick={() => {
                            setNotificationsOpen(false);
                            navigate('/dashboard/notifications');
                          }}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {notification.message || notification.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {new Date(notification.createdAt).toLocaleString('sr-RS', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-primary-600 flex-shrink-0 mt-2"></div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/dashboard/notifications');
                      }}
                      className="w-full px-4 py-2 rounded-xl text-sm font-semibold text-primary-600 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    >
                      {t('notifications.viewAll') || 'View all'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
              aria-label={t('nav.logout') || 'Logout'}
            >
              <MdLogout className="text-xl" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 flex lg:flex-row overflow-hidden relative">
        {/* Sidebar - Desktop only; mobile uses bottom tab bar */}
        <aside className="hidden lg:flex lg:static flex-col overflow-hidden m-4 rounded-xl shadow-lg bg-white dark:bg-gray-800 h-auto max-h-[calc(100vh-6rem)] flex-shrink-0 w-64 relative">
          {/* Decorative sidebar background (bottom flowers) */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-bottom bg-no-repeat bg-contain opacity-70 dark:opacity-35 z-0"
            style={{ backgroundImage: `url(${backSideFlowers})` }}
          />

          {/* Navigation */}
          <nav className="relative z-10 flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {desktopMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActiveDesktop(item.path);
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => navigate(item.path)}
                      className={`
                        relative w-full flex items-center gap-3 px-4 py-3 rounded-xl
                        transition-colors
                        ${active
                          ? 'bg-primary-100/60 dark:bg-primary-900/10 text-gray-900 dark:text-gray-100'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                        }
                      `}
                    >
                      {active && (
                        <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-primary-500" />
                      )}
                      <Icon className={`text-xl ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`} />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Page Content - centered with constrained width; padding-bottom on mobile for tab bar + gap */}
        <main className="flex-1 overflow-y-auto min-w-0 relative w-full pb-28 lg:pb-0">
          <div className={`w-full mx-auto px-4 sm:px-5 lg:px-6 py-1 ${location.pathname.includes('/conversations') ? 'max-w-[1400px]' : location.pathname.includes('/jobs') ? 'max-w-[1600px]' : 'max-w-6xl'}`}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile: Floating bottom tab bar */}
      <nav className="lg:hidden fixed bottom-2 left-0 right-0 z-50 px-2 pb-safe pt-2">
        <div className="max-w-lg mx-auto flex items-center justify-around gap-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 px-2">
          {mobileMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const isSettings = item.isSettings;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`
                  flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1.5 px-2 rounded-xl transition-colors
                  ${isSettings
                    ? active
                      ? 'bg-primary-100/80 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    : active
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }
                `}
              >
                <Icon className="text-xl flex-shrink-0" />
                <span className="text-[10px] font-medium truncate max-w-full">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title={t('auth.logoutConfirmTitle') || 'Potvrda odjave'}
        message={t('auth.logoutConfirmMessage') || 'Da li ste sigurni da želite da se odjavite?'}
        confirmText={t('auth.logout') || 'Odjavi se'}
        cancelText={t('common.cancel') || t('common.no') || 'Otkaži'}
        type="warning"
      />
    </div>
  );
}

